import React from 'react';
import styled from 'styled-components';
import instagramIcon from '../../assets/icons/instagram.svg';
import youtubeIcon from '../../assets/icons/youtube.svg';
import tiktokIcon from '../../assets/icons/titok.svg';
import moveArrowIcon from '../../assets/icons/move_arrow.svg';
import {
  ContentCardThumbnail,
  isYoutubePlatform,
} from './ContentCardThumbnail';
import {
  getContentThumbnailUrl,
  openExternalLink,
  type ContentItem,
} from '../../utils/listingData';

const PLATFORM_META: Record<string, { label: string; icon: string }> = {
  YOUTUBE: { label: 'Youtube', icon: youtubeIcon },
  INSTAGRAM: { label: 'Instagram', icon: instagramIcon },
  TIKTOK: { label: 'TikTok', icon: tiktokIcon },
};

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const CreatorName = styled.p`
  flex: 1;
  min-width: 0;
  height: 96px;
  overflow: hidden;
  color: #ffffff;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 28px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.7px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 22px;
    height: 72px;
  }
`;

const CardArrowCircle = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.lime};
  transition: background 0.25s ease;
`;

const CardArrow = styled.img`
  width: 24px;
  height: 24px;
  filter: brightness(0);
  transition: filter 0.25s ease;
`;

const ContentCard = styled.a`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 710px;
  padding: 24px 20px 20px;
  border-radius: 28px;
  background: #111111;
  text-decoration: none;
  color: inherit;
  transition: background 0.25s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.lime};

    ${CreatorName} {
      color: #111111;
    }

    ${CardArrowCircle} {
      background: #111111;
    }

    ${CardArrow} {
      filter: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 480px;
  }
`;

const CardMedia = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  border-radius: 20px;
  overflow: hidden;
  background: #000000;
`;

const PlatformBadge = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
`;

const PlatformIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const PlatformName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
`;

const formatCreatorName = (name: string) =>
  name.startsWith('@') ? name : `@${name}`;

const getPlatformMeta = (platform: string) =>
  PLATFORM_META[platform.toUpperCase()] ?? {
    label: platform,
    icon: instagramIcon,
  };

interface ContentGridCardProps {
  item: ContentItem;
}

const ContentGridCard: React.FC<ContentGridCardProps> = ({ item }) => {
  const platform = getPlatformMeta(item.platform);
  const isYoutube = isYoutubePlatform(item.platform);

  return (
    <ContentCard
      href={item.content_url || undefined}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
        openExternalLink(event, item.content_url)
      }
    >
      <CardHeader>
        <CreatorName>{formatCreatorName(item.name)}</CreatorName>
        <CardArrowCircle aria-hidden>
          <CardArrow src={moveArrowIcon} alt="" />
        </CardArrowCircle>
      </CardHeader>
      <CardMedia>
        <ContentCardThumbnail
          $isYoutube={isYoutube}
          src={getContentThumbnailUrl(item)}
          alt={item.name}
          loading="lazy"
        />
        <PlatformBadge>
          <PlatformIcon src={platform.icon} alt="" />
          <PlatformName>{platform.label}</PlatformName>
        </PlatformBadge>
      </CardMedia>
    </ContentCard>
  );
};

export default ContentGridCard;
